import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", part_number: "", source: "local", company: "", cost: "" };

export const PendingPartsBuilder = ({ parts, onChange }) => {
  const [form, setForm] = useState(empty);
  const [adding, setAdding] = useState(false);

  const add = () => {
    if (!form.name.trim()) {
      toast.error("Part name is required");
      return;
    }
    onChange([...parts, { ...form, name: form.name.trim() }]);
    setForm(empty);
    setAdding(false);
  };

  return (
    <div className="space-y-2" data-testid="pending-parts-builder">
      {parts.length > 0 && (
        <div className="space-y-1.5">
          {parts.map((p, i) => (
            <div key={i} className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2" data-testid={`pending-part-${i}`}>
              <span className="text-sm font-medium text-slate-900 truncate">{p.name}</span>
              {p.part_number && <span className="text-sm font-mono text-slate-600">{p.part_number}</span>}
              <Badge variant="outline" className="rounded-full text-[10px] px-2 py-0 capitalize">{p.source}</Badge>
              {p.company && <span className="text-xs text-slate-500 truncate">from {p.company}</span>}
              {p.cost !== "" && <span className="text-xs text-slate-600">${Number(p.cost).toFixed(2)}</span>}
              <button type="button" className="ml-auto text-red-600" onClick={() => onChange(parts.filter((_, j) => j !== i))} data-testid={`pending-part-remove-${i}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
      {adding ? (
        <div className="rounded-md border border-blue-200 bg-blue-50/50 p-3 space-y-2">
          <Input placeholder="Part name * (e.g. Drain pump)" value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} data-testid="pending-part-name" />
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Part #" value={form.part_number} onChange={(e) => setForm((s) => ({ ...s, part_number: e.target.value }))} />
            <Select value={form.source} onValueChange={(v) => setForm((s) => ({ ...s, source: v }))}>
              <SelectTrigger data-testid="pending-part-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="local">Local (pick up)</SelectItem>
                <SelectItem value="online">Online (order)</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Company / store" value={form.company} onChange={(e) => setForm((s) => ({ ...s, company: e.target.value }))} />
            <Input placeholder="Cost $" type="number" value={form.cost} onChange={(e) => setForm((s) => ({ ...s, cost: e.target.value }))} />
          </div>
          <div className="flex gap-2">
            <Button type="button" onClick={add} className="bg-blue-700 hover:bg-blue-800 flex-1" data-testid="pending-part-add-save">Add part</Button>
            <Button type="button" variant="outline" onClick={() => { setAdding(false); setForm(empty); }}>Cancel</Button>
          </div>
        </div>
      ) : (
        <Button type="button" variant="outline" onClick={() => setAdding(true)} className="w-full gap-2 border-dashed" data-testid="pending-part-add-btn">
          <Plus className="w-4 h-4" /> Add part
        </Button>
      )}
    </div>
  );
};
